import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Is there a SAINT® TMS clinic in Fort Lauderdale?",
    answer:
      "Neuro Wellness TMS Centers of America does not have a Fort Lauderdale office. The clinic is located in Coral Springs, which is in northern Broward County — roughly 30 to 40 minutes from Fort Lauderdale by car. Because SAINT® is delivered over five consecutive days rather than six weeks, some Fort Lauderdale-area patients find it practical to drive up for the treatment week or stay locally rather than commuting daily for a traditional TMS course.",
  },
  {
    question: "How far is Coral Springs from Fort Lauderdale?",
    answer:
      "Coral Springs is approximately 30 to 40 minutes from Fort Lauderdale by car, depending on traffic. The drive is typically via I-95 North or the Florida Turnpike to the Sawgrass Expressway. Fort Lauderdale patients in the northern part of the city, such as those near Tamarac or North Lauderdale, are even closer — sometimes 15 to 20 minutes away.",
  },
  {
    question: "Can I commute from Fort Lauderdale daily for SAINT®?",
    answer:
      "It is possible, since the drive is shorter than from Miami. However, SAINT® involves ten sessions per day with roughly 50-minute breaks, meaning you are at the clinic for most of each day. Some Fort Lauderdale patients commute daily; others prefer to stay near the clinic for the five-day week to avoid rush-hour traffic and have a quieter experience. The choice depends on your schedule and how you feel during treatment.",
  },
  {
    question:
      "What is the difference between SAINT® and traditional TMS for Fort Lauderdale patients?",
    answer:
      "Traditional TMS is one session per day, five days a week, for about six weeks — roughly 36 visits. SAINT® delivers 50 short sessions over five consecutive days with fMRI-guided targeting. For a Fort Lauderdale patient, traditional TMS may be easier to commute to daily, while SAINT® requires a concentrated one-week commitment. The right choice depends on your diagnosis, schedule, insurance, and clinical eligibility. Neuro Wellness offers both.",
  },
  {
    question: "Do I need an MRI before SAINT® if I live in Fort Lauderdale?",
    answer:
      "Yes. SAINT® uses a functional MRI to compute your individualized stimulation target. The MRI must be completed before the treatment week begins. Some Fort Lauderdale patients complete the scan at a local imaging center if approved by the care team; others have it done near the clinic in Coral Springs. The Neuro Wellness team coordinates this step with you.",
  },
  {
    question: "Does insurance cover SAINT® for Fort Lauderdale patients?",
    answer:
      "Coverage depends on your insurance plan, not your city. SAINT® is billed under Category III CPT codes, and coverage varies widely between plans. Some patients self-pay. Neuro Wellness verifies your benefits before treatment so you understand your cost-sharing, regardless of whether your plan is based in Broward County or elsewhere. Benefits verification does not guarantee payment.",
  },
];

export const saintFortLauderdale: LearnPageData = {
  slug: "saint-tms-fort-lauderdale",
  title: "SAINT® TMS for Fort Lauderdale Patients | Neuro Wellness",
  description:
    "Fort Lauderdale patients: SAINT® TMS is available in Coral Springs, about 30–40 minutes away. Learn the drive, the 5-day schedule, and how SAINT® compares to traditional TMS.",
  canonical: "/locations/saint-tms-fort-lauderdale",
  h1: "SAINT® TMS for Patients from Fort Lauderdale",
  breadcrumbLabel: "SAINT® TMS from Fort Lauderdale",
  crumbs: [
    { name: "Home", href: "/" },
    { name: "Patient Education", href: "/learn" },
    { name: "SAINT® TMS from Fort Lauderdale", href: "" },
  ],

  shortAnswer:
    "Neuro Wellness TMS Centers of America is located in Coral Springs, in northern Broward County — approximately 30 to 40 minutes from Fort Lauderdale by car. Fort Lauderdale-area patients travel to the clinic for SAINT® treatment, an FDA-cleared, fMRI-guided accelerated TMS protocol delivered over five consecutive days. Because the course is compressed into one week rather than spread over six weeks, some patients find it practical to drive up for the treatment week. A consultation and MRI are required before treatment begins.",
  keyTakeaways: [
    "Neuro Wellness is in Coral Springs, roughly 30–40 minutes from Fort Lauderdale — no Fort Lauderdale office.",
    "SAINT® is a five-day accelerated protocol, which can be more practical than six weeks of daily traditional TMS visits.",
    "Fort Lauderdale patients in the northern part of the city are as close as 15–20 minutes to the clinic.",
    "A psychiatric consultation and fMRI scan are required before the treatment week.",
    "Neuro Wellness offers both SAINT® and traditional TMS, so the clinical team can help you choose what fits.",
  ],

  sections: [
    {
      heading: "Where Neuro Wellness Is Located",
      body: (
        <>
          {P(
            "Neuro Wellness TMS Centers of America is located at 9734 W Sample Rd, 2nd Floor, Suite B, Coral Springs, FL 33065. Coral Springs is in northern Broward County, about 30 to 40 minutes from Fort Lauderdale by car.",
          )}
          {P(
            "The clinic does not have a Fort Lauderdale office. Patients from Fort Lauderdale and surrounding areas — including Tamarac, North Lauderdale, Lauderdale Lakes, and Oakland Park — regularly travel to Coral Springs for treatment. Fort Lauderdale patients in the northern part of the city are sometimes as close as 15 to 20 minutes away.",
          )}
        </>
      ),
    },
    {
      heading: "Why Fort Lauderdale Patients Consider SAINT®",
      body: (
        <>
          {P(
            "SAINT® is an FDA-cleared accelerated TMS protocol that delivers 50 short sessions over five consecutive days, using fMRI-guided targeting to personalize the stimulation site. It is available at a limited number of verified providers because it requires the Magnus Neuromodulation System, brain imaging, and trained staff.",
          )}
          {P(
            "For Fort Lauderdale patients whose depression has not adequately improved with antidepressant medication, SAINT® offers a compressed timeline: one week instead of six. This can be appealing for people who cannot step away from work or family for two months, or who are in a severe episode and want a faster path to treatment.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/what-is-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                what SAINT® is
              </a>{" "}
              and how it{" "}
              <a
                href="/learn/saint-vs-traditional-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                differs from traditional TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Commuting from Fort Lauderdale for Treatment",
      body: (
        <>
          {P(
            "The drive from Fort Lauderdale to Coral Springs is typically 30 to 40 minutes via I-95 North or the Florida Turnpike to the Sawgrass Expressway. Traffic on I-95 during morning and evening rush hours can add time, so patients often plan their schedule around off-peak travel.",
          )}
          {P(
            "Because SAINT® involves ten sessions per day with roughly 50-minute breaks, you are at the clinic for most of each day. Some Fort Lauderdale patients commute daily; others prefer to stay at a hotel near the clinic for the five-day week to avoid traffic and have a more restful experience. The choice depends on your energy level, schedule, and personal preference.",
          )}
          {P(
            <>
              See our{" "}
              <a
                href="/traveling-for-treatment"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                traveling for treatment guide
              </a>{" "}
              for planning details.
            </>,
          )}
        </>
      ),
    },
    {
      heading: "SAINT® vs Traditional TMS for Fort Lauderdale Patients",
      body: (
        <>
          {P(
            "Neuro Wellness offers both SAINT® and traditional TMS, so Fort Lauderdale patients have options. Traditional TMS is one session per day, five days a week, for about six weeks — roughly 36 visits. For someone who lives 30 minutes away, that is a manageable daily commute. Traditional TMS is also more broadly covered by insurance.",
          )}
          {P(
            "SAINT® delivers 50 sessions in five consecutive days with fMRI-guided targeting. It requires a concentrated one-week commitment and an MRI, and many patients self-pay because insurance coverage is still limited. The right choice depends on your diagnosis, schedule, insurance, and clinical eligibility. A consultation helps you weigh both.",
          )}
          {P(
            <>
              Read our full{" "}
              <a
                href="/learn/saint-vs-traditional-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT® vs traditional TMS comparison
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "What the Consultation Involves",
      body: (
        <>
          {P(
            "The first step is a consultation with the Neuro Wellness clinical team. A psychiatrist reviews your diagnosis, medication history, and overall health, and screens for factors that may affect eligibility — such as bipolar disorder, seizure history, or metal implants. They discuss whether SAINT® or traditional TMS is more appropriate for your situation.",
          )}
          {P(
            "Some Fort Lauderdale patients complete the initial consultation by telehealth and travel to Coral Springs for the MRI and treatment week. The care team coordinates what can be done remotely versus what requires an in-person visit.",
          )}
          {P(
            <>
              You can{" "}
              <a
                href="/am-i-a-candidate"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                request a consultation online
              </a>{" "}
              or call 954-827-9793.
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Outpatient Treatment in a Private Setting",
      body: (
        <>
          {P(
            "Neuro Wellness is a private outpatient clinic, not a hospital. Treatment takes place in dedicated suites with a care team that coordinates your schedule, insurance verification, and follow-up. The environment is calm and focused, designed for patients who want a supportive setting during their treatment week.",
          )}
          {P(
            "For Fort Lauderdale patients, the clinic can help with scheduling, local accommodations, and coordination with your home providers so that follow-up care continues smoothly after the treatment week.",
          )}
        </>
      ),
    },
    {
      heading: "Neuro Wellness TMS Centers of America",
      body: (
        <>
          {P(
            "Neuro Wellness TMS Centers of America is an outpatient mental health treatment center in Coral Springs, Florida. The clinic offers SAINT® on the Magnus Neuromodulation System, along with traditional TMS, SPRAVATO® (esketamine), Prism for PTSD, Proliv Rx at-home neuromodulation, IV ketamine infusions, psychotherapy, and medication management.",
          )}
          {P(
            "The clinic serves patients from Broward, Palm Beach, and Miami-Dade counties, including Fort Lauderdale, Tamarac, Pompano Beach, and Deerfield Beach. To learn whether SAINT® may be appropriate for you, request a consultation with the clinical team.",
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Magnus Medical. SAINT® Technology and Verified Provider information.",
      url: "https://magnusmedical.com",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/locations/saint-tms-fort-lauderdale#webpage`,
        name: "SAINT® TMS for Patients from Fort Lauderdale",
        url: `${SITE_URL}/locations/saint-tms-fort-lauderdale`,
        description:
          "Fort Lauderdale patients: SAINT® TMS is available in Coral Springs, about 30–40 minutes away. Learn the drive, the 5-day schedule, and how SAINT® compares to traditional TMS.",
        about: [
          { "@id": `${SITE_URL}/locations/saint-tms-fort-lauderdale#therapy` },
          { "@id": `${SITE_URL}/#condition-mdd` },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/locations/saint-tms-fort-lauderdale#therapy`,
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
        alternateName: ["SAINT TMS", "Stanford Neuromodulation Therapy (SNT)"],
        description:
          "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System with SAINT Technology (FDA 510(k) K220177): 10 sessions per day for 5 consecutive days.",
        relevantSpecialty: "Psychiatric",
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-mdd`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      faqJsonld(faqs, `${SITE_URL}/locations/saint-tms-fort-lauderdale#faq`),
      breadcrumbJsonld([
        { name: "Home", href: "/" },
        { name: "Patient Education", href: "/learn" },
        {
          name: "SAINT® TMS from Fort Lauderdale",
          href: "/locations/saint-tms-fort-lauderdale",
        },
      ]),
    ],
  },
  relatedLinks: [
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
    {
      title: "SAINT® TMS in Florida",
      href: "/saint-tms-florida",
      description: "SAINT® treatment for patients across Florida",
    },
    {
      title: "SAINT® vs Traditional TMS",
      href: "/learn/saint-vs-traditional-tms",
      description: "Key differences in schedule, targeting, and duration",
    },
    {
      title: "Traveling for Treatment",
      href: "/traveling-for-treatment",
      description: "Planning your visit to Coral Springs",
    },
    {
      title: "Does Insurance Cover SAINT®?",
      href: "/learn/does-insurance-cover-saint-tms",
      description: "Coverage, prior authorization, and benefits verification",
    },
    {
      title: "Request a Consultation",
      href: "/am-i-a-candidate",
      description: "Find out if SAINT® may be appropriate for you",
    },
  ],
};
